import { SlicePipe } from '@angular/common';
import { Component, signal, SimpleChanges } from '@angular/core';

import { componentOutletConfigFactory } from '@core/common-legacy';
import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import {
  AvatarComponent,
  ButtonComponent,
  type DataTableCellContext,
  EMPTY_CELL,
} from '@core/user-interface-legacy';
import type { AskTicketBoardItem, TellentMemberBrief } from '@tellent-ask-hr/core-types';

import { AsksColumnId, defineAskTableColumn } from '../types';

/* eslint-disable @angular-eslint/no-inputs-metadata-property */
/* eslint-disable @typescript-eslint/consistent-type-assertions */

@Component({
  selector: 'assignees-column',
  imports: [AvatarComponent, ButtonComponent, SlicePipe, TranslatePipe],
  template: `
    @for (
      assignee of expanded() ? assignees : (assignees | slice: 0 : expandButtonThreshold);
      track assignee.guid
    ) {
      <div layout layout-align-items-center>
        <rt-avatar
          [type]="assignee.avatarThumbUrl ? 'image' : 'initials'"
          size="small"
          [initials]="initials()[$index]"
          [imageSrc]="assignee.avatarThumbUrl"
          margin-right-small
        />
        {{ assignee?.firstName }} {{ assignee?.lastName }}
      </div>
    }
    @if (assignees.length > expandButtonThreshold) {
      <rt-button (click)="toggleExpanded($event)">
        {{
          expanded()
            ? ('tlc.ask_hr.requests.show_less' | translate)
            : ('tlc.ask_hr.requests.show_all'
              | translate: { counter: assignees.length - expandButtonThreshold })
        }}
      </rt-button>
    }
  `,
  inputs: AssigneesColumnComponent.NgInputs,
  styleUrl: './assignees-column.component.less',
})
export class AssigneesColumnComponent {
  public static NgInputs = ['assignees' as const];

  public assignees: TellentMemberBrief[];
  public expandButtonThreshold = 2;

  protected initials = signal<string[]>([]);
  protected expanded = signal(false);

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.assignees && this.assignees) {
      this.initials.set(
        this.assignees.map(assignee => {
          const { firstName, lastName } = assignee;
          return `${firstName?.[0] || ''}${lastName?.[0] || ''}`;
        }),
      );
    }
  }

  protected toggleExpanded(event: MouseEvent) {
    event.stopPropagation();
    this.expanded.update(expanded => !expanded);
  }
}

const componentFactory = ({ row }: DataTableCellContext<AskTicketBoardItem>) => {
  if (!row.assignees.length) return EMPTY_CELL;

  return componentOutletConfigFactory(AssigneesColumnComponent, {
    inputs: {
      assignees: row.assignees,
    },
  });
};

export const assignedToColumn = defineAskTableColumn(() => ({
  id: AsksColumnId.AssignedTo,
  label: injectTranslate()('tlc.ask_hr.requests.assigned_to'),
  visible: true,
  geometry: {
    width: 250,
    minWidth: 200,
    autoFitProp: 'width',
  },
  options: {
    draggable: true,
    resizable: true,
    removable: false,
    sortable: false,
  },
  componentFactory,
}));

export const assignedToUserColumn = defineAskTableColumn(() => ({
  id: AsksColumnId.AssignedTo,
  label: injectTranslate()('tlc.ask_hr.requests.assigned_to'),
  visible: true,
  geometry: {
    width: 220,
    minWidth: 200,
    autoFitProp: 'width',
  },
  options: {
    draggable: false,
    resizable: false,
    removable: false,
    sortable: false,
  },
  componentFactory,
}));
